import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LoaderCircle, LucideAngularModule, Phone, PhoneOff } from 'lucide-angular';

import { ContactAvatarComponent } from '@shared/components/contact-avatar/contact-avatar.component';
import { WhatsappIconComponent } from '@shared/components/icons/whatsapp-icon/whatsapp-icon.component';

import { VoiceShellService, VoiceWaitingCall } from '../../services/voice-shell.service';

/**
 * Desktop ring card. Drops in from the top of the viewport the moment a call waits for
 * the agent (or for anyone), whatever page they are on — dashboard, audience, settings —
 * and offers the two things a phone offers: answer, or let it ring for someone else.
 * Answering lands the inbox on the caller's conversation.
 *
 * Dark on purpose: the whole app is light chrome, so a black card with the emerald
 * "a call is live" accent reads as an event, not as another panel. Mirrors the mobile
 * full-screen ring in tone; sized for a cursor and a corner of the eye.
 */
@Component({
    selector: 'app-voice-incoming-call',
    standalone: true,
    imports: [TranslatePipe, LucideAngularModule, ContactAvatarComponent, WhatsappIconComponent],
    templateUrl: './voice-incoming-call.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'contents' },
    styles: [
        `
            @keyframes ring-card-enter {
                0% {
                    opacity: 0;
                    transform: translate(-50%, -18px) scale(0.98);
                }
                100% {
                    opacity: 1;
                    transform: translate(-50%, 0) scale(1);
                }
            }
            @keyframes ring-card-ripple {
                0% {
                    opacity: 0.6;
                    transform: scale(1);
                }
                100% {
                    opacity: 0;
                    transform: scale(1.9);
                }
            }
            @keyframes ring-card-breathe {
                0%,
                100% {
                    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45);
                }
                60% {
                    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
                }
            }
            @keyframes ring-card-shake {
                0%,
                18%,
                100% {
                    transform: rotate(0deg);
                }
                3%,
                9%,
                15% {
                    transform: rotate(-14deg);
                }
                6%,
                12% {
                    transform: rotate(14deg);
                }
            }
            .ring-card {
                animation: ring-card-enter 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
            }
            .ring-card-mesh {
                background:
                    radial-gradient(
                        70% 90% at 12% 0%,
                        rgba(16, 185, 129, 0.32) 0%,
                        rgba(16, 185, 129, 0) 60%
                    ),
                    radial-gradient(
                        50% 80% at 100% 100%,
                        rgba(49, 87, 218, 0.22) 0%,
                        rgba(49, 87, 218, 0) 65%
                    );
            }
            .ring-card-grid {
                background-image: radial-gradient(
                    circle at center,
                    rgba(255, 255, 255, 0.13) 1px,
                    transparent 1.4px
                );
                background-size: 18px 18px;
                mask-image: radial-gradient(80% 90% at 20% 30%, #000 10%, transparent 100%);
                -webkit-mask-image: radial-gradient(80% 90% at 20% 30%, #000 10%, transparent 100%);
            }
            .ring-card-ripple {
                animation: ring-card-ripple 2.2s cubic-bezier(0.22, 1, 0.36, 1) infinite;
            }
            .ring-card-answer {
                animation: ring-card-breathe 2s ease-out infinite;
            }
            .ring-card-answer-icon {
                animation: ring-card-shake 2.8s ease-in-out infinite;
                transform-origin: 50% 55%;
            }
            @media (prefers-reduced-motion: reduce) {
                .ring-card,
                .ring-card-ripple,
                .ring-card-answer,
                .ring-card-answer-icon {
                    animation: none;
                }
                .ring-card {
                    transform: translate(-50%, 0);
                }
            }
        `,
    ],
})
export class VoiceIncomingCallComponent {
    protected readonly shell = inject(VoiceShellService);

    protected readonly PhoneIcon = Phone;
    protected readonly PhoneOffIcon = PhoneOff;
    protected readonly LoaderIcon = LoaderCircle;

    protected readonly incoming = this.shell.incoming;
    protected readonly othersWaiting = this.shell.othersWaiting;

    protected waitLabel(call: VoiceWaitingCall): string {
        const sec = this.shell.waitSecOf(call);
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    }

    /** Headline: the contact when we know them, else the number in readable groups. */
    protected headline(call: VoiceWaitingCall): string {
        if (call.name && call.name !== call.from) return call.name;
        return this.prettyNumber(call.from);
    }

    /** The number as a sub-line only when the headline is a name. */
    protected numberLine(call: VoiceWaitingCall): string {
        if (!call.from || call.name === call.from || !call.name) return '';
        return this.prettyNumber(call.from);
    }

    /** "+34 600 00 00 00" — readable groups instead of a raw E.164 blob. */
    protected prettyNumber(from: string): string {
        if (!from) return '';
        const digits = from.replace(/[^\d+]/g, '');
        const m = /^(\+\d{1,3})(\d+)$/.exec(digits);
        if (!m) return from;
        const rest = m[2].replace(/(\d{3})(?=\d)/g, '$1 ').trim();
        return `${m[1]} ${rest}`;
    }

    protected answer(call: VoiceWaitingCall) {
        if (!this.shell.isPickupReady(call) || this.shell.isPickingUp(call)) return;
        this.shell.requestNotificationPermission();
        this.shell.answer(call);
    }

    protected ignore(call: VoiceWaitingCall) {
        this.shell.dismiss(call);
    }

    protected showQueue() {
        this.shell.queueExpanded.set(true);
    }
}
